import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setBalanceUpdate } from "redux/user/slice";
import { useNotificationsStore } from "stores/notifications";
import { useLocalizationStore } from "stores/localization";
import io, { Socket } from "socket.io-client";
import { BalanceResponse } from "types/Balance";
import { NotificationItem } from "types/Notifications";
import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { getLocalStorageItem } from "utils/localStorage";
import { setSocketServerAddress } from "utils/socketServerAdress";
import {
  addReferral,
  deleteReferral,
  updateReferral,
} from "../redux/referral/slice";

type WebSocketProps = {
  token: string;
};

const useWebSocket = ({ token }: WebSocketProps): Socket | null => {
  const socketRef = useRef<Socket | null>(null);
  const dispatch = useDispatch();
  const addNotification = useNotificationsStore(
    (state) => state.addNotification,
  );

  const spamMode = localStorage.getItem("spamMode");
  const spamSystem = localStorage.getItem("spamSystem");
  const spamBonus = localStorage.getItem("spamBonus");
  const language =
    useLocalizationStore((store) => store.language) ||
    getLocalStorageItem(STORAGE_LANGUAGE_NAME);

  useEffect(() => {
    if (!token) {
      return;
    }

    const socketServerAddress = setSocketServerAddress();

    const socket = io(socketServerAddress, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      auth: {
        token,
        spam: Boolean(spamMode),
        spamSystem: Boolean(spamSystem),
        spamBonus: Boolean(spamBonus),
      },
      query: {
        userLanguage: language.toLowerCase(),
      },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 20000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      // eslint-disable-next-line no-console
      console.log("✅ Connected to WebSocket");
      socket.emit("subscribeForBalanceUpdates", {});
      socket.emit("subscribeForPopupNotifications", {});
      socket.emit("subscribeForReferralUpdates", {});
    });
    socket.on("connect_timeout", (timeout) => {
      // eslint-disable-next-line no-console
      console.error("❌ Connection timeout:", timeout);
    });
    socket.on("disconnect", (reason) => {
      // eslint-disable-next-line no-console
      console.warn("⚠️ Disconnected:", reason);
      // if (reason === "io server disconnect") {
      //   // сервер сам отключил — нужно ручное переподключение
      //   socket.connect();
      // }
    });

    socket.on("connect_error", (error) => {
      // eslint-disable-next-line no-console
      console.error("❌ Connection error:", error);
    });

    socket.on("WS_BALANCE_UPDATE", (balance: BalanceResponse) => {
      // eslint-disable-next-line no-console
      console.log("WS_BALANCE_UPDATE", balance);

      const updatedBalance = {
        ...balance,
        balance: balance.balance,
        bonusBalance: balance.bonusBalance,
        referrerBalance: balance.referrerBalance,
      };
      dispatch(setBalanceUpdate(updatedBalance));
    });

    socket.on("WS_REFERRAL_ADDED", (data) => {
      // eslint-disable-next-line no-console
      console.log("WS_REFERRAL_ADDED", data);
      dispatch(addReferral(data));
    });

    socket.on("WS_REFERRALS_UPDATE", (data) => {
      // eslint-disable-next-line no-console
      console.log("WS_REFERRALS_UPDATE", data);
      dispatch(updateReferral(data));
    });

    socket.on("WS_REFERRAL_DELETED", (data) => {
      // eslint-disable-next-line no-console
      console.log("WS_REFERRAL_DELETED", data);
      const { referralId } = data;
      dispatch(deleteReferral(Number(referralId)));
    });

    socket.on("WS_POPUP_TRIGGERED", (data: NotificationItem) => {
      // eslint-disable-next-line no-console
      console.log("📩 WS_POPUP_TRIGGERED", data);
      if (data) {
        addNotification(data);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit("unsubscribeFromBalanceUpdates", {});
        socketRef.current.emit("unsubscribeForReferralUpdates", {});
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
      }
    };
  }, [
    token,
    language,
    spamMode,
    spamSystem,
    spamBonus,
    dispatch,
    addNotification,
  ]);

  useEffect(() => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit("userLanguageChanged", {
        userLanguage: language.toLowerCase(),
      });
    }
  }, [language]);

  return null;
};

export default useWebSocket;
